import { inject, Injectable } from '@angular/core';
import { forkJoin, from, Observable, of } from 'rxjs';
import { ApiCacheService } from './api-cache.service';
import { EligibilityFieldsObject } from './api-contracts';
import { FeatureFlagService } from '@brightside-web/desktop/data-access/core-services';
import { KnownFeatureFlags } from '@brightside-web/desktop/data-access/shared';

import { BsCacheService, SimpleGlobalModalService } from '@brightside-web/desktop/data-access/core-services';
import { map } from 'rxjs/operators';

/**
 * When `'1'`, data-collect responses get a synthetic `profession` field if the API omitted it (dev menu).
 * Synced with DevMenuService; avoids dev-settings ↔ data-access circular imports.
 */
export const DEV_INJECT_PROFESSION_SESSION_KEY = 'brightside_dev_inject_profession';

export interface ProdEligResponseInterface {
  products: ProdEligObjInterface[];
}

export interface ProdEligObjInterface {
  name: string;
  required_fields?: ProdEligReqFieldsInterface[];
}

export interface ProdEligReqFieldsInterface {
  name: string;
  api_parameter_name: string;
  unique_identifier_flag?: boolean;
  sub_fields?: EligibilityFieldsObject[];
  not_replayable?: boolean;
  value?: string;
}

export enum ProductPostIntroMapping {
  '/external-accounts/intro/launch' = 'cashFlow',
  '/home/savings/consents' = 'payrollSavings',
  '/credit-setup/consents' = 'creditScore',
  '/loans/consents' = 'loan',
}

export enum ProductCodes {
  PayrollSavings = 'payrollSavings',
  Loan = 'loan',
  Spending = 'spending',
  CreditScore = 'creditScore',
  CashFlow = 'cashFlow',
  CashAdvance = 'cashAdvance',
  Invite = 'invite',
  Registration = 'registration',
}

export const nonRequiredProductFields: string[] = ['middle_name', 'address2'];

@Injectable({
  providedIn: 'root',
})
export class ProductEligibilityService {
  completedVerifications: string[] = [];

  apiPath = '/client/product/data-collect?fieldVersion=1.2';

  private readonly _PRODUCT_ELIGIBILITY_CACHE_TTL_MS = 30 * 1000;
  private _productEligibilityCache: { response: ProdEligResponseInterface; timestamp: number } | null = null;
  private bsCacheService: BsCacheService = inject(BsCacheService);
  private globalModalSvc: SimpleGlobalModalService = inject(SimpleGlobalModalService);
  private featureFlagService: FeatureFlagService = inject(FeatureFlagService);

  constructor(private apiCache: ApiCacheService) {
    const routeKeys = Object.keys(ProductPostIntroMapping);
    const routeKeyCache$: Observable<any>[] = [];

    routeKeys.forEach((cacheKey) => {
      routeKeyCache$.push(from(this.bsCacheService.getItem(cacheKey)));
    });

    this.featureFlagService.getFlag(KnownFeatureFlags.ENABLESOCCOLLECTIONINAPPS).subscribe((enableSocCollectionInApps) => {
      if (enableSocCollectionInApps) {
        this.apiPath = '/client/product/data-collect?fieldVersion=1.3';
      }
    });
  }

  getProductEligibility(): Observable<ProdEligResponseInterface>;
  getProductEligibility(productCode: ProductCodes): Observable<ProdEligObjInterface | undefined>;
  getProductEligibility(productCode?: ProductCodes): Observable<ProdEligResponseInterface | ProdEligObjInterface | undefined> {
    const now = Date.now();
    const cached = this._productEligibilityCache;
    if (cached && now - cached.timestamp < this._PRODUCT_ELIGIBILITY_CACHE_TTL_MS) {
      const adjusted = this._applyProfessionDevOverride(cached.response);
      if (productCode) {
        return of(adjusted.products.find((product) => product.name === productCode));
      }
      return of(adjusted);
    }

    return from(this.apiCache.get<ProdEligResponseInterface>('api-mobile', this.apiPath, {})).pipe(
      map((response) => {
        this._productEligibilityCache = { response, timestamp: Date.now() };
        const adjusted = this._applyProfessionDevOverride(response);
        if (productCode) {
          return adjusted.products.find((product) => product.name === productCode);
        } else {
          return adjusted;
        }
      })
    );
  }

  /** Default body field for dev-injected profession (align with backend when known). */
  private static readonly _DEV_INJECT_PROFESSION: ProdEligReqFieldsInterface = {
    name: 'profession',
    api_parameter_name: 'soc_code',
  };

  /**
   * When dev inject is on, appends `profession` to each product’s `required_fields` only if not already present.
   * Cached raw API response is unchanged; this runs on read.
   */
  private _applyProfessionDevOverride(response: ProdEligResponseInterface): ProdEligResponseInterface {
    if (typeof sessionStorage === 'undefined' || sessionStorage.getItem(DEV_INJECT_PROFESSION_SESSION_KEY) !== '1') {
      return response;
    }
    return {
      products: response.products.map((p) => this._injectProfessionIntoProductIfMissing(p)),
    };
  }

  private _injectProfessionIntoProductIfMissing(p: ProdEligObjInterface): ProdEligObjInterface {
    const fields = p.required_fields;
    if (fields?.some((f) => f.name === 'profession')) {
      return p;
    }
    const injected = { ...ProductEligibilityService._DEV_INJECT_PROFESSION };
    if (fields == null) {
      return { ...p, required_fields: [injected] };
    }
    return { ...p, required_fields: [...fields, injected] };
  }

  getProductCodeRedirect(productCode: string): string {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const redirectPath = Object.keys(ProductPostIntroMapping)[Object.values(ProductPostIntroMapping).indexOf(productCode)];
    return redirectPath ? redirectPath : '/';
  }

  hasMissingFields(productCode: ProductCodes, wantNull: boolean = false): Observable<boolean | null> {
    return this.getProductEligibility(productCode).pipe(
      map((fields) => {
        if (wantNull && fields?.required_fields === null) {
          return null;
        } else if (fields?.required_fields || fields?.required_fields === null) {
          return this.checkFieldsForEmpty(fields.required_fields);
        } else {
          throw Error('Product not found');
        }
      })
    );
  }

  /** Clears cached data-collect response (e.g. after dev menu toggles profession inject). */
  invalidateProductEligibilityCache(): void {
    this._productEligibilityCache = null;
  }

  showVerifyIntroModal(ctaActionFunc: Function) {
    this.globalModalSvc.createModal({
      title: 'VERIFY_IDENTITY_INTRO_TITLE',
      headerImagePath: './assets/data-verification.png',
      contentKey: 'VERIFY_IDENTITY_INTRO_DESC',
      displayCloseButton: false,
      eventName: 'identity_verification_halfsheet',
      isError: false,
      size: 'medium',
      ctaKey: 'BUTTON_CONTINUE',
      ctaAction: () => {
        ctaActionFunc();
      },
    });
  }

  testReqValueForEmpty(field: ProdEligReqFieldsInterface): boolean {
    return (
      field.value === null ||
      (!field.value && !nonRequiredProductFields.includes(field.name)) ||
      (field.name === 'middle_name' && field.value === 'middle')
    );
  }

  checkFieldsForEmpty(fields: ProdEligReqFieldsInterface[]): boolean {
    if (fields === null) {
      return true;
    }
    for (const field of fields) {
      if (field.sub_fields) {
        for (const subField of field.sub_fields) {
          if (!subField.not_replayable) {
            if (this.testReqValueForEmpty(subField)) {
              return true;
            }
          }
        }
      } else if (field.api_parameter_name) {
        if (!field.not_replayable) {
          if (this.testReqValueForEmpty(field)) {
            return true;
          }
        }
      }
    }
    return false;
  }
}
